import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import BackupRestoreLeftovers from './BackupRestoreLeftovers';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { WarningCircle, CheckCircle, ArrowsClockwise, CloudArrowUp, Play, ShieldCheck } from '@phosphor-icons/react';
import { fmtStamp } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

function fmtSize(bytes) {
  if (bytes == null) return '-';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return '-';
  return fmtStamp(iso);
}

const STATUS_LABEL = { success: 'Sucesso', error: 'Falhou', running: 'A correr', skipped: 'Ignorado' };
const TRIGGER_LABEL = { scheduled: 'Automático', startup_recovery: 'Recuperação', manual: 'Manual' };
const STATUS_CLS = {
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  error: 'bg-red-50 text-red-700 border-red-200',
  running: 'bg-amber-50 text-amber-700 border-amber-200',
  skipped: 'bg-brand-bg text-brand-muted border-brand-border',
};

export default function AdminBackupPage() {
  useRequireAdmin();
  const [health, setHealth] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [testingId, setTestingId] = useState(null);

  const fetchAll = useCallback(async (silent = false) => {
    try {
      const [h, hist] = await Promise.all([
        axios.get(`${API}/api/admin/backup/health`),
        axios.get(`${API}/api/admin/backup/history`),
      ]);
      setHealth(h.data);
      setHistory(hist.data?.runs || hist.data || []);
    } catch {
      if (!silent) toast.error('Erro ao carregar o estado dos backups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const anyTesting = history.some((r) => r.restore_test_status === 'running');
  useEffect(() => {
    if (!anyTesting) return undefined;
    const t = setInterval(() => fetchAll(true), 3000);
    return () => clearInterval(t);
  }, [anyTesting, fetchAll]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/backup/run`);
      if (data.status === 'success') toast.success('Backup concluído com sucesso');
      else if (data.status === 'skipped') toast.warning('Backup ignorado: ' + (data.reason || 'já está a correr noutra instância'));
      else toast.error('Backup falhou: ' + (data.error || 'erro desconhecido'));
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao iniciar o backup');
    } finally {
      setRunning(false);
    }
  };

  const testRestore = async (id) => {
    setTestingId(id);
    try {
      await axios.post(`${API}/api/admin/backup/${id}/test-restore`);
      toast.info('Teste de restauro iniciado, o resultado aparece na linha');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao iniciar o teste de restauro');
    } finally {
      setTestingId(null);
      fetchAll(true);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6" data-testid="admin-backup-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading text-brand-text">Backups da base de dados</h1>
            <p className="text-sm text-brand-muted mt-1">Backup automático diário (03:00 UTC), encriptado e enviado para um destino externo.</p>
          </div>
          <button
            onClick={runNow}
            disabled={running}
            data-testid="run-backup-now-btn"
            className="bg-brand-green text-white rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-60"
          >
            {running ? <ArrowsClockwise size={16} className="animate-spin" /> : <Play size={16} />}
            {running ? 'A fazer backup…' : 'Fazer backup agora'}
          </button>
        </div>

        {loading && <p className="text-sm text-brand-muted">A carregar…</p>}

        {!loading && health && (
          <div
            data-testid="backup-health-banner"
            className={`rounded-2xl border p-4 flex items-start gap-3 ${health.healthy ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}
          >
            {health.healthy ? <CheckCircle size={22} className="text-emerald-600 mt-0.5 shrink-0" /> : <WarningCircle size={22} className="text-red-600 mt-0.5 shrink-0" />}
            <div className="text-sm">
              {health.healthy ? (
                <p className="text-emerald-800 font-medium">Tudo bem. Último backup com sucesso: {fmtDate(health.last_success?.started_at)}.</p>
              ) : (
                <div>
                  <p className="text-red-800 font-medium">Atenção: há um problema com os backups.</p>
                  <ul className="list-disc list-inside text-red-700 mt-1 space-y-0.5">
                    {health.reasons.map((r, i) => <li key={i} data-testid={`backup-health-reason-${i}`}>{r}</li>)}
                  </ul>
                </div>
              )}
              {!health.ftp_configured && (
                <p className="text-amber-700 mt-2 text-xs">O envio para FTP está desativado ou não configurado neste ambiente: os backups ficam só localmente no servidor.</p>
              )}
            </div>
          </div>
        )}

        {!loading && (
          <div className="bg-white rounded-2xl border border-brand-border overflow-hidden">
            <div className="px-4 py-3 border-b border-brand-border text-sm font-medium text-brand-text">Histórico de execuções</div>
            <div className="divide-y divide-brand-border" data-testid="backup-history-list">
              {history.length === 0 && <p className="text-sm text-brand-muted p-4">Ainda não há execuções registadas.</p>}
              {history.map((run) => {
                const sum = run.restore_test_summary || {};
                return (
                  <div key={run.id} className="px-4 py-3 text-sm space-y-1.5" data-testid={`backup-run-${run.id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <span className={`inline-block border rounded-full px-2.5 py-0.5 text-xs font-medium mr-2 ${STATUS_CLS[run.status] || ''}`}>
                          {STATUS_LABEL[run.status] || run.status}
                        </span>
                        <span className="text-brand-text">{fmtDate(run.started_at)}</span>
                        <span className="text-brand-muted ml-2 text-xs" data-testid={`backup-trigger-${run.id}`}>
                          · {TRIGGER_LABEL[run.trigger] || 'Não registado'}
                        </span>
                        {run.method && <span className="text-brand-muted ml-2 text-xs">({run.method === 'python_fallback' ? 'fallback Python' : run.method})</span>}
                      </div>
                      <div className="flex items-center gap-3 text-brand-muted text-xs">
                        <span>{fmtSize(run.size_bytes)}</span>
                        {run.uploaded && <span className="flex items-center gap-1 text-emerald-600"><CloudArrowUp size={14} /> FTP</span>}
                        {run.status === 'error' && run.error && <span className="text-red-600 max-w-xs truncate" title={run.error}>{run.error}</span>}
                        {run.status === 'success' && (
                          <button
                            onClick={() => testRestore(run.id)}
                            disabled={testingId !== null || anyTesting}
                            data-testid={`test-restore-btn-${run.id}`}
                            className="border border-brand-border rounded-full px-3 py-1 text-xs font-medium text-brand-text flex items-center gap-1.5 hover:bg-brand-bg disabled:opacity-50"
                          >
                            {testingId === run.id || run.restore_test_status === 'running' ? <ArrowsClockwise size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
                            {testingId === run.id || run.restore_test_status === 'running' ? 'A testar…' : 'Testar restauro'}
                          </button>
                        )}
                      </div>
                    </div>
                    {run.restore_test_status && (
                      <p
                        data-testid={`restore-test-result-${run.id}`}
                        className={`text-xs ${run.restore_test_status === 'success' ? 'text-emerald-700' : run.restore_test_status === 'running' ? 'text-amber-700' : 'text-red-600'}`}
                      >
                        Teste de restauro ({fmtDate(run.restore_test_at)}):{' '}
                        {run.restore_test_status === 'running'
                          ? 'a testar… (pode demorar alguns minutos, a página atualiza sozinha)'
                          : run.restore_test_status === 'success'
                          ? `sucesso — ${sum.collections ?? 0} coleções e ${sum.documents_restored ?? 0} documentos comparados (${sum.documents_at_backup ?? 0} no backup)`
                          : `erro — ${sum.error || (sum.mismatches?.length ? `contagens diferentes em ${sum.mismatches.join(', ')}` : 'falhou')}`}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <BackupRestoreLeftovers />
      </div>
    </AdminLayout>
  );
}
